import {ChevronRightIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {useCallback, useEffect, useEffectEvent} from 'react'

import {SubIssueExpandCollapseToggleStatKey} from '../../api/stats/contracts'
import {useTableCopilotTasks} from '../../features/copilot-tasks/use-table-copilot-tasks'
import {useExpandedRows} from '../../features/sub-issues/use-expanded-rows'
import {useTableHierarchy} from '../../features/sub-issues/use-table-hierarchy'
import {usePostStats} from '../../hooks/common/use-post-stats'
import {usePaginatedMemexItemsQuery} from '../../state-providers/memex-items/queries/use-paginated-memex-items-query'
import styles from './expand-button.module.css'

export type ExpandButtonProps = {
  /** Ref to the underlying expand button */
  expandRef?: React.RefObject<HTMLButtonElement | null>
  /** Whether or not this item is expanded. */
  expanded: boolean
  /** Whether or not this item can be expanded. If false, expand button will not be rendered. */
  expandable: boolean
  /** The database ID of the issue for this row. */
  issueId?: string
  /** The NWO reference of the issue for this row. */
  nwoReference?: string
  /** Unique ID for this row (should match what's in TanStack Table) */
  rowId: string
}

export function ExpandButton({expandRef, expanded, expandable, issueId, nwoReference, rowId}: ExpandButtonProps) {
  const {hierarchyEnabled} = useTableHierarchy()
  const {copilotTasksEnabled} = useTableCopilotTasks()
  const {toggleRowExpanded} = useExpandedRows()
  const {setPageParamsForSubIssues} = usePaginatedMemexItemsQuery()
  const {postStats} = usePostStats()

  const canExpandSubIssues = hierarchyEnabled && !!issueId && !!nwoReference
  const canExpand = expandable && (canExpandSubIssues || copilotTasksEnabled)

  const onClick = useCallback(() => {
    if (!canExpand) return
    if (canExpandSubIssues && !expanded) {
      setPageParamsForSubIssues(issueId, nwoReference)
    }
    toggleRowExpanded(rowId)

    // Emit stats for expand/collapse
    postStats({
      name: SubIssueExpandCollapseToggleStatKey,
      context: JSON.stringify({issueId, nwoReference, expanded: !expanded}),
    })
  }, [
    canExpand,
    canExpandSubIssues,
    expanded,
    toggleRowExpanded,
    rowId,
    setPageParamsForSubIssues,
    postStats,
    issueId,
    nwoReference,
  ])

  const onExpandedChange = useEffectEvent(() => {
    if (expanded && canExpandSubIssues) {
      setPageParamsForSubIssues(issueId, nwoReference)
    }
  })

  useEffect(() => {
    onExpandedChange()
  }, [expanded])

  if (!canExpand) return null

  // Add keyboard support for expanding/collapsing rows when focus is on the button
  const onKeyDown = (e: React.KeyboardEvent) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    switch (e.key) {
      case 'Enter':
        e.preventDefault()
        e.stopPropagation()
        onClick()
        break
      case 'ArrowRight':
        if (!expanded) {
          e.preventDefault()
          e.stopPropagation()
          onClick()
        }
        break
      case 'ArrowLeft':
        if (expanded) {
          e.preventDefault()
          e.stopPropagation()
          onClick()
        }
        break
      default:
        break
    }
  }

  return (
    /**
     * Disable the tooltip since the purpose of the button is sufficiently obvious in context.
     * When the tooltip doesn't have enough space to render on the left, it renders on the right.
     * This blocks the click target and makes it easier to accidentally trigger title editing.
     * Retain the accessible name for screenreaders.
     * https://github.com/github/sub-issues/issues/912
     */
    // eslint-disable-next-line primer-react/a11y-remove-disable-tooltip
    <IconButton
      ref={expandRef}
      icon={ChevronRightIcon}
      aria-label={expanded ? 'Collapse' : 'Expand'}
      aria-expanded={expanded}
      onClick={onClick}
      unsafeDisableTooltip
      variant="invisible"
      className={expanded ? `${styles.button} ${styles.expanded}` : styles.button}
      onKeyDown={onKeyDown}
    />
  )
}
